/**
 * Per-automation preference forms (Automations Module Revamp, Phase 2).
 *
 * `PreferenceForm` is a fully-controlled form keyed by preset kind. It edits the typed,
 * server-validated preferences object for one automation, drawing every enum option and bound
 * from `@xavior/schema` (the single source of truth shared with the API validator + run-time
 * compiler). The parent owns the value and the Save mutation — this component only renders the
 * inputs and reports edits back through `onChange`.
 */
import type { ChangeEvent } from "react";
import {
  EMAIL_TONES,
  TEXT_LENGTHS,
  EMAIL_SCOPES,
  EMAIL_VOICES,
  MEETING_FORMATS,
  DEPTHS,
  PRESENTATIONS,
  PREFERENCE_LIMITS,
  type AutomationPreferences,
  type DailyBriefPreferences,
  type EmailAutoDraftPreferences,
  type MeetingPrepPreferences,
  type OvernightPreferences,
} from "@xavior/schema";
import type { PresetKind } from "./redesign/fixtures";
import { ChipInput } from "./ChipInput";
import { useContactSuggestions } from "./useContactSuggestions";
import { TimeWheel } from "./TimeWheel";

// ── Small shared helpers ──────────────────────────────────────────────────────

/** "all-relevant" → "All relevant"; "professional" → "Professional" (sentence case). */
function prettyOption(value: string): string {
  const spaced = value.replace(/-/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function EnumSelect<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  defaultValue,
  formatOption = prettyOption,
}: {
  id: string;
  label: string;
  value: T | undefined;
  options: readonly T[];
  onChange: (next: T | undefined) => void;
  /** When set, the field has a CONCRETE default and no "Default" option — an unset value shows
   *  this default (e.g. email tone=professional, length=medium, scope=all-relevant). Omit it to
   *  keep the optional "Default" (no-preference) choice. */
  defaultValue?: T;
  /** Override the option label (default: title-case the value). */
  formatOption?: (value: T) => string;
}) {
  const hasDefault = defaultValue !== undefined;
  const effective = hasDefault ? value ?? defaultValue : value;
  return (
    <div className="prefs__field">
      <label className="prefs__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="prefs__select"
        value={effective ?? ""}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onChange((e.target.value || undefined) as T | undefined)
        }
      >
        {!hasDefault && <option value="">Default</option>}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {formatOption(opt)}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  onChange: (next: number | undefined) => void;
}) {
  return (
    <div className="prefs__field">
      <label className="prefs__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="prefs__input"
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const n = Number(e.target.value);
          onChange(e.target.value === "" || Number.isNaN(n) ? undefined : n);
        }}
      />
    </div>
  );
}

function CheckGroup<K extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: ReadonlyArray<{ key: K; label: string }>;
  value: Partial<Record<K, boolean>> | undefined;
  onChange: (next: Partial<Record<K, boolean>>) => void;
}) {
  return (
    <fieldset className="prefs__field prefs__checks">
      <legend className="prefs__label">{legend}</legend>
      {options.map(({ key, label }) => (
        <label key={key} className="prefs__check">
          <input
            type="checkbox"
            checked={!!value?.[key]}
            onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
          />
          {label}
        </label>
      ))}
    </fieldset>
  );
}

// ── Per-kind forms ─────────────────────────────────────────────────────────────

function DailyBriefForm({
  value,
  onChange,
}: {
  value: DailyBriefPreferences;
  onChange: (next: DailyBriefPreferences) => void;
}) {
  const set = (patch: Partial<DailyBriefPreferences>) =>
    onChange({ ...value, ...patch });
  return (
    <>
      <CheckGroup
        legend="Sections"
        value={value.sections}
        onChange={(sections) => set({ sections })}
        options={[
          { key: "day", label: "Your day" },
          { key: "inbox", label: "Inbox" },
          { key: "tasks", label: "Tasks" },
          { key: "events", label: "Events" },
        ]}
      />
      <ChipInput
        id="prefs-news-topics"
        label="News topics"
        value={value.newsTopics}
        max={PREFERENCE_LIMITS.maxTopics}
        placeholder="e.g. AI policy, interest rates"
        onChange={(newsTopics) => set({ newsTopics })}
      />
      <TimeWheel
        id="prefs-delivery-time"
        label="Delivery time"
        value={value.deliveryTime}
        onChange={(deliveryTime) => set({ deliveryTime })}
      />
    </>
  );
}

function EmailAutoDraftForm({
  value,
  onChange,
}: {
  value: EmailAutoDraftPreferences;
  onChange: (next: EmailAutoDraftPreferences) => void;
}) {
  const set = (patch: Partial<EmailAutoDraftPreferences>) =>
    onChange({ ...value, ...patch });
  // Focus people resolves against existing contacts (debounced typeahead); the two topic chip
  // inputs stay free-text (no suggestion props).
  const { suggestions, onQueryChange } = useContactSuggestions();
  return (
    <>
      <EnumSelect
        id="prefs-voice"
        label="Response style"
        value={value.voice}
        options={EMAIL_VOICES}
        onChange={(voice) => set({ voice })}
        defaultValue="xavior"
        formatOption={(v) => (v === "human" ? "Human response" : "Xavior response")}
      />
      <EnumSelect
        id="prefs-tone"
        label="Tone"
        value={value.tone}
        options={EMAIL_TONES}
        onChange={(tone) => set({ tone })}
        defaultValue="professional"
      />
      <EnumSelect
        id="prefs-length"
        label="Length"
        value={value.length}
        options={TEXT_LENGTHS}
        onChange={(length) => set({ length })}
        defaultValue="medium"
      />
      <EnumSelect
        id="prefs-scope"
        label="Scope"
        value={value.scope}
        options={EMAIL_SCOPES}
        onChange={(scope) => set({ scope })}
        defaultValue="all-relevant"
      />
      <ChipInput
        id="prefs-focus-people"
        label="Focus people"
        value={value.focusPeople}
        max={PREFERENCE_LIMITS.maxFocusPeople}
        placeholder="e.g. Dana W., priya@acme.com"
        onChange={(focusPeople) => set({ focusPeople })}
        suggestions={suggestions}
        onQueryChange={onQueryChange}
      />
      <div className="prefs__field">
        <label className="prefs__label" htmlFor="prefs-signature">
          Signature
        </label>
        <textarea
          id="prefs-signature"
          className="prefs__textarea xv-scroll"
          rows={3}
          maxLength={PREFERENCE_LIMITS.maxSignatureLen}
          value={value.signature ?? ""}
          onChange={(e) => set({ signature: e.target.value || undefined })}
        />
        <p className="prefs__hint">
          {/* The signature is the user's own sign-off — applied only to "Human response"
              drafts. "Xavior response" drafts sign off as Xavior, so the signature is unused.
              voice defaults to "xavior" (so undefined → the Xavior copy). */}
          {value.voice === "human"
            ? `Added to the end of each reply. Up to ${PREFERENCE_LIMITS.maxSignatureLen} characters.`
            : `Used only with the Human response style — Xavior replies sign off as Xavior. Up to ${PREFERENCE_LIMITS.maxSignatureLen} characters.`}
        </p>
      </div>
    </>
  );
}

function MeetingPrepForm({
  value,
  onChange,
}: {
  value: MeetingPrepPreferences;
  onChange: (next: MeetingPrepPreferences) => void;
}) {
  const set = (patch: Partial<MeetingPrepPreferences>) =>
    onChange({ ...value, ...patch });
  return (
    <>
      <EnumSelect
        id="prefs-format"
        label="Format"
        value={value.format}
        options={MEETING_FORMATS}
        onChange={(format) => set({ format })}
      />
      <NumberField
        id="prefs-lead-time"
        label="Lead time (minutes)"
        value={value.leadTimeMinutes}
        min={PREFERENCE_LIMITS.leadTimeMin}
        max={PREFERENCE_LIMITS.leadTimeMax}
        onChange={(leadTimeMinutes) => set({ leadTimeMinutes })}
      />
      <CheckGroup
        legend="Sections"
        value={value.sections}
        onChange={(sections) => set({ sections })}
        options={[
          { key: "who", label: "Who" },
          { key: "why", label: "Why" },
          { key: "references", label: "References" },
          { key: "headsUp", label: "Heads-up" },
        ]}
      />
      <EnumSelect
        id="prefs-depth"
        label="Depth"
        value={value.depth}
        options={DEPTHS}
        onChange={(depth) => set({ depth })}
      />
    </>
  );
}

function OvernightForm({
  value,
  onChange,
}: {
  value: OvernightPreferences;
  onChange: (next: OvernightPreferences) => void;
}) {
  const set = (patch: Partial<OvernightPreferences>) =>
    onChange({ ...value, ...patch });
  return (
    <>
      <ChipInput
        id="prefs-pinned-topics"
        label="Pinned topics"
        value={value.pinnedTopics}
        max={PREFERENCE_LIMITS.maxPinnedTopics}
        placeholder="e.g. competitor launches, hiring"
        onChange={(pinnedTopics) => set({ pinnedTopics })}
      />
      <NumberField
        id="prefs-max-reports"
        label="Reports per night"
        value={value.maxReportsPerNight}
        min={PREFERENCE_LIMITS.maxReportsMin}
        max={PREFERENCE_LIMITS.maxReportsMax}
        onChange={(maxReportsPerNight) => set({ maxReportsPerNight })}
      />
      <EnumSelect
        id="prefs-presentation"
        label="Presentation"
        value={value.presentation}
        options={PRESENTATIONS}
        onChange={(presentation) => set({ presentation })}
      />
      <EnumSelect
        id="prefs-research-depth"
        label="Research depth"
        value={value.researchDepth}
        options={DEPTHS}
        onChange={(researchDepth) => set({ researchDepth })}
      />
      <TimeWheel
        id="prefs-overnight-delivery-time"
        label="Delivery time"
        value={value.deliveryTime}
        onChange={(deliveryTime) => set({ deliveryTime })}
      />
    </>
  );
}

// ── Public entry: dispatch by preset kind ──────────────────────────────────────

export function PreferenceForm({
  kind,
  value,
  onChange,
}: {
  kind: PresetKind;
  value: AutomationPreferences;
  onChange: (next: AutomationPreferences) => void;
}) {
  switch (kind) {
    case "daily_briefing":
      return (
        <DailyBriefForm
          value={value as DailyBriefPreferences}
          onChange={onChange}
        />
      );
    case "inbox_watcher":
      return (
        <EmailAutoDraftForm
          value={value as EmailAutoDraftPreferences}
          onChange={onChange}
        />
      );
    case "meeting_prep":
      return (
        <MeetingPrepForm
          value={value as MeetingPrepPreferences}
          onChange={onChange}
        />
      );
    case "overnight_processing":
      return (
        <OvernightForm
          value={value as OvernightPreferences}
          onChange={onChange}
        />
      );
    default:
      return null;
  }
}
